import React from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined'

import { scopeTypes, userRightTypes } from '../../../constants'
import { Logger, LoggerTypes } from '../../../utils'
import { checkPermissions } from '../../../components/AccessControl'

import {
  ButtonIcon,
  DialogDelete,
  Button,
  buttonModes,
  buttonTypes,
  DataGrid,
  Icon,
  iconNames
} from '../../../components'
import { AccessControl } from '../../../components/AccessControl'
import './style.scss'

export const GlobalAttributeList = ({
  attributeName,
  items,
  columns,
  columnVisibilityModel,
  extraDialogAttributeDelete,
  extraDialogAttributeUpdate,
  DialogUpdate,
  onOpenDialog,
  selectedAttribute,
  onGlobalsImport
}) => {
  const { t } = useTranslation()

  /**
   * @see GlobalAttributeTabsDialog.onGlobalsImport()
   * @param {array<uuid>} ids Array of attribute UUID to select.
   * @param {object} current Current attribute object selected.
   */
  const onSelect = (ids, current) => {
    let globalImport = {}
    globalImport[attributeName] = ids

    Logger.trace(LoggerTypes.CONTROLLER, 'GlobalAttributeList.onSelect()', { ids, current, globalImport })

    onGlobalsImport(globalImport)
  }

  // -- create an array of selected UUID
  // -- this array is required by MUI DataGrid to select the checkboxes.
  // -- only add to this array the uuid that are under the scope = 'GLOBAL'
  // --
  let selectionModel = []
  ;(selectedAttribute || []).forEach((el) => {
    if (el.id) {
      if (el?.scope === scopeTypes.GLOBAL || !el.scope) {
        selectionModel.push(el.id)
      } else if (el?.scope === scopeTypes.IMPORT) {
        if (el.parent_id) {
          selectionModel.push(el.parent_id)
        } else if (el.id) {
          selectionModel.push(el.id)
        }
      }
    }
  })

  Logger.trace(LoggerTypes.CONTAINER, 'GlobalAttributeList.render()', {
    items,
    columns,
    columnVisibilityModel,
    extraDialogAttributeDelete,
    extraDialogAttributeUpdate,
    selectedAttribute,
    selectionModel
  })

  return (
    <div className="GlobalAttributeList">
      <AccessControl userPermissions={[userRightTypes.GLOBAL_ATTRIBUTES_EDIT]} renderNoAccess={() => <></>}>
        {onOpenDialog && (
          <Button
            className="buttonAdd"
            mode={buttonModes.TABS_ACTION}
            type={buttonTypes.BUTTON}
            text={t('common.create')}
            tooltip={t(`attributes.${attributeName}.create.global`)}
            icon={<Icon name={iconNames.PLUS} size={13} className="buttonsAdd-icon" />}
            onClick={onOpenDialog}
          />
        )}
      </AccessControl>
      <DataGrid
        isLoading={false}
        rows={items}
        checkboxSelection={selectedAttribute !== null}
        selectionModel={selectionModel}
        onSelectionModelChange={onSelect}
        columns={columns}
        columnVisibilityModel={columnVisibilityModel}
        actionsButtons={[
          <ButtonIcon
            disabled={!checkPermissions([userRightTypes.GLOBAL_ATTRIBUTES_EDIT])}
            key={'button-edit'}
            DialogClass={DialogUpdate}
            icon={<ModeEditOutlineOutlinedIcon />}
            tooltip={t('common.update')}
            extraDialogAttribute={extraDialogAttributeUpdate}
          />,
          <ButtonIcon
            disabled={!checkPermissions([userRightTypes.GLOBAL_ATTRIBUTES_DELETE])}
            key={'button-delete'}
            DialogClass={DialogDelete}
            icon={<DeleteOutlineIcon />}
            tooltip={t('common.delete')}
            extraDialogAttribute={extraDialogAttributeDelete}
          />
        ]}
        hideFooterPagination={true}
        disableColumnFilter={true}
        exportData={false}
      />
    </div>
  )
}

GlobalAttributeList.displayName = 'GlobalAttributeList'

GlobalAttributeList.propTypes = {
  items: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  columnVisibilityModel: PropTypes.object.isRequired,
  attributeName: PropTypes.oneOf(['resources', 'products', 'variables', 'locations', 'weather_data', 'parameters'])
    .isRequired,
  extraDialogAttributeDelete: PropTypes.object.isRequired,
  DialogUpdate: PropTypes.any.isRequired,
  extraDialogAttributeUpdate: PropTypes.object.isRequired,
  onOpenDialog: PropTypes.func,
  selectedAttribute: PropTypes.arrayOf(PropTypes.object),
  onGlobalsImport: PropTypes.func
}

GlobalAttributeList.defaultProps = {
  items: null,
  columns: null,
  columnVisibilityModel: null,
  attributeName: 'resources',
  DialogUpdate: () => {},
  extraDialogAttributeUpdate: null,
  extraDialogAttributeDelete: {},
  onOpenDialog: null,
  selectedAttribute: [],
  onGlobalsImport: () => {}
}
